import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Card, CardContent } from '@saas-kit/ui';

import { QmsPrintPortal } from './QmsPrintPortal';
import { QmsTokenReceipt } from './QmsTokenReceipt';
import type { QmsBranding, QmsToken } from './types';
import { usePrintToken } from './usePrintToken';

interface Props {
  token: QmsToken;
  serviceName: string;
  branding: QmsBranding | null;
  locale: string;
  onIssueAnother: () => void;
}

/** Success state shown after a token is issued. The receipt prints once
 *  automatically on mount; the Print button is a manual reprint. */
export function IssuedTokenCard({
  token,
  serviceName,
  branding,
  locale,
  onIssueAnother,
}: Props): JSX.Element {
  const { t } = useTranslation('qms');
  const { print, isPrinting } = usePrintToken();
  const printed = useRef(false);

  useEffect(() => {
    if (printed.current) return;
    printed.current = true;
    // Defer one beat so the legacy-fallback receipt DOM is mounted first.
    const timer = window.setTimeout(() => {
      void print(token.id, { interactive: false });
    }, 150);
    return () => window.clearTimeout(timer);
  }, [print, token.id]);

  return (
    <>
      <Card data-testid="qms-issue-success">
        <CardContent className="flex flex-col items-center gap-4 py-10 text-center">
          <p className="text-sm uppercase tracking-wide text-muted-foreground">
            {t('issue.success_label')}
          </p>
          <p className="font-mono text-6xl font-bold text-foreground">
            {token.display_code}
          </p>
          <p className="text-lg text-foreground">{serviceName}</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Button
              variant="secondary"
              disabled={isPrinting}
              onClick={() => void print(token.id)}
            >
              {t('print.now')}
            </Button>
            <Button onClick={onIssueAnother}>
              {t('issue.issue_another')}
            </Button>
          </div>
        </CardContent>
      </Card>
      <QmsPrintPortal>
        <QmsTokenReceipt
          token={token}
          serviceName={serviceName}
          branding={branding}
          locale={locale}
        />
      </QmsPrintPortal>
    </>
  );
}
